/* eslint-disable complexity */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Textbox from 'carbon-react/lib/components/textbox';

import { LiveReportList } from '@futrli-sdk/predict';

import { HelpLinkSection } from '../../help-link-section/help-link-section';
import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFormValidationErrorSummary } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-validation-error-summary/wizard-form-validation-error-summary';
import { TEMPLATE_FORM_FIELD_IDS } from '../constants';
import { CreateTemplateSidebarMode } from '../create-template-sidebar';
import { TemplateSourceSelect } from './template-source-select';
import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

export interface TemplateFormScreenProps {
  error?: string;
  validationErrors?: Record<string, { id: string; message: string }>;
  templateName: string;
  selectedReportId: string;
  liveReports: LiveReportList[];
  onTemplateNameChange: (value: string) => void;
  onSelectedReportIdChange: (value: string) => void;
  onClose: () => void;
  onCreate: () => void;
  onBlur?: () => void;
  mode?: CreateTemplateSidebarMode;
  onNavigateToCreateReport: () => void;
  isReportSelectDisabled?: boolean;
}

export const TemplateFormScreen = ({
  error,
  validationErrors = {},
  templateName,
  selectedReportId,
  liveReports,
  onTemplateNameChange,
  onSelectedReportIdChange,
  onClose,
  onCreate,
  onBlur,
  mode = 'report',
  onNavigateToCreateReport,
  isReportSelectDisabled = false
}: TemplateFormScreenProps) => {
  const intl = useIntl();
  const isDashboard = mode === 'dashboard';

  return (
    <WizardScreen
      footerMode={FooterMode.CREATE}
      onCancel={onClose}
      onCreate={onCreate}
      createText={<FormattedMessage id="common.create" defaultMessage="Create" />}
    >
      <>
        {error && <WizardFormError message={error} mb={3} />}

        <WizardFormValidationErrorSummary errors={validationErrors} mb={3} />

        <WizardRequiredFieldInfo />

        <Box mb={3}>
          <Textbox
            label={intl.formatMessage({
              id: 'reporting.template_name',
              defaultMessage: 'Template name'
            })}
            name={TEMPLATE_FORM_FIELD_IDS.TEMPLATE_NAME}
            id={TEMPLATE_FORM_FIELD_IDS.TEMPLATE_NAME}
            value={templateName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onTemplateNameChange(e.target.value)}
            onBlur={onBlur}
            error={validationErrors.name?.message}
            required
          />
        </Box>

        <Box mb={3}>
          <TemplateSourceSelect
            liveReports={liveReports}
            selectedReportId={selectedReportId}
            onSelectedReportIdChange={onSelectedReportIdChange}
            onNavigateToCreateReport={onNavigateToCreateReport}
            error={validationErrors.selectedReport?.message}
            onBlur={onBlur}
            mode={mode}
            disabled={isReportSelectDisabled}
          />
        </Box>

        <HelpLinkSection
          title={
            isDashboard
              ? intl.formatMessage({
                  id: 'reporting.creating_dashboard_template_first_time',
                  defaultMessage: 'Creating a dashboard template for the first time?'
                })
              : intl.formatMessage({
                  id: 'reporting.creating_report_template_first_time',
                  defaultMessage: 'Creating a report template for the first time?'
                })
          }
          href={
            isDashboard
              ? 'https://support.futrli.com/en/articles/14597207-create-a-dashboard-template'
              : 'https://support.futrli.com/en/articles/9527936-create-a-workspace-template'
          }
        />
      </>
    </WizardScreen>
  );
};
